<script setup lang="ts">
/**
 * DocRef (IR-116 #9) — THE document-name idiom: one compact file chip for every
 * structured surface that names a document (Complétude lists, RemediationRow
 * footer + doc-action rows, the summary's missing-documents list and gaps
 * dialog). Markdown-rendered doc names get the matching look via
 * `Markdown.vue`'s `docRefs` code-span restyle — same visual, different carrier.
 *
 * **It IS a `RefChip`** (MYAC-1172): the border, the hover accent, the
 * role/tabindex and the click contract come from the shared chip, whose
 * `document` kind carries this chip's own size and surface tint. It was a
 * lookalike — the same markup written twice, which is how the two drifted into
 * opposite click contracts in the first place. What stays here is what is
 * genuinely about a DOCUMENT and about nothing else: the two natures, and the
 * measured truncation below.
 *
 * Click affordance only when `clickable`: most sites just NAME the document
 * (no navigation exists yet), and a hover accent on a dead chip is a lie.
 *
 * Two document NATURES (IR-116 #16): a `reference` is a real file (solid
 * border, surface tint, file icon); a `concept` is a searched-for document
 * NOTION with no real file behind it — missing-doc names, TO_CREATE names,
 * the sought side of «Documents à trouver» — and must visibly NOT read as a
 * file: dashed border, no fill, search icon, italic name.
 *
 * A chip whose name is visually TRUNCATED tooltips its full name (Opus review
 * on ConsistencyFindingRow) — an explicit `title` prop still wins. Truncation
 * is measured (scrollWidth vs clientWidth), re-checked on resize and on name
 * change; jsdom measures 0/0, so tests keep the explicit-`title` behavior.
 *
 * A `handle` makes a real file openable (MYAC-1640): the click raises
 * `OpenDocumentMenu`, which reads the document and offers "Open in SharePoint"
 * (or says why it cannot). A `concept` has no file to open, handle or not. The
 * menu mounts on the first click only: a page citing many documents should not
 * carry one popup per chip it may never open.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { DocumentHandle } from '@/models/DocumentHandle'
import OpenDocumentMenu from './OpenDocumentMenu.vue'
import RefChip from './RefChip.vue'

const props = defineProps<{
  name: string
  title?: string
  clickable?: boolean
  /** 'reference' (default) = a real file · 'concept' = a document notion. */
  kind?: 'reference' | 'concept'
  /** Where the file lives: when set, a click offers to open it at its source. */
  handle?: DocumentHandle | null
}>()

const emit = defineEmits<{ click: [] }>()

const opensMenu = computed(() => !!props.handle && props.kind !== 'concept')
const menuMounted = ref(false)
const openMenu = ref<InstanceType<typeof OpenDocumentMenu> | null>(null)

async function onClick(event: Event): Promise<void> {
  if (props.clickable) emit('click')
  const handle = props.handle
  if (!opensMenu.value || !handle) return
  // Read now: the dispatch is over once the menu has mounted.
  const target = event.currentTarget instanceof HTMLElement ? event.currentTarget : undefined
  menuMounted.value = true
  await nextTick()
  openMenu.value?.toggle(event, handle, target)
}

/** True when the name span actually clips — drives the fallback tooltip. */
const nameEl = ref<HTMLElement | null>(null)
const truncated = ref(false)
function measure(): void {
  const el = nameEl.value
  truncated.value = el !== null && el.scrollWidth > el.clientWidth
}
let observer: ResizeObserver | null = null
onMounted(() => {
  measure()
  if (typeof ResizeObserver !== 'undefined' && nameEl.value) {
    observer = new ResizeObserver(measure)
    observer.observe(nameEl.value)
  }
})
onBeforeUnmount(() => observer?.disconnect())
watch(
  () => props.name,
  () => void nextTick(measure),
)

const tooltip = computed<string | undefined>(
  () => props.title ?? (truncated.value ? props.name : undefined),
)
</script>

<template>
  <RefChip
    kind="document"
    :concept="kind === 'concept'"
    :interactive="!!clickable || opensMenu"
    :title="tooltip"
    :aria-haspopup="opensMenu ? 'menu' : undefined"
    data-testid="doc-ref"
    @click="onClick"
  >
    <span ref="nameEl" class="min-w-0 truncate" :class="kind === 'concept' ? 'italic' : ''">{{
      name
    }}</span>
    <!-- Inside the chip so DocRef keeps one root; the popup teleports to the body. -->
    <OpenDocumentMenu v-if="menuMounted && opensMenu" ref="openMenu" />
  </RefChip>
</template>
